import React, { createContext, useContext, useState } from 'react';

type Language = 'en' | 'es';

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    // Navigation
    home: 'Home',
    menu: 'Menu',
    locations: 'Locations',
    about: 'About Us',
    contact: 'Contact',
    blog: 'Blog',
    
    // Footer
    quickLinks: 'Quick Links',
    followUs: 'Follow Us',
    contactUs: 'Contact Us',
    rightsReserved: 'All Rights Reserved',
    
    // Home
    heroTitle: 'Craft Cocktails & Modern American Fare',
    heroSubtitle: 'Where great food meets good times',
    orderNow: 'Order Online',
    viewMenu: 'View Menu',
    reserveTable: 'Reserve a Table',
    ourStory: 'Our Story',
    storyText: 'Capital District Social is more than just a bar and restaurant—it\'s a community gathering place where great food meets good times. We\'ve created a space where friends become family and every visit feels like coming home.',
    privateEvents: 'Private Events',
    privateEventsDesc: 'Our private dining spaces are perfect for any occasion! From intimate gatherings to large corporate parties, we can help!',
    reserveNow: 'Reserve Now!',
    followSocial: 'Follow Our Social Media',
    followSocialDesc: 'Stay connected! Follow us on social media to never miss out on our latest promotions, events, and specials.',
    drinksTitle: 'Craft Cocktails and Premium Spirits Made to Keep the Good Times Flowing',
    ourDrinks: 'Our Drinks',
    cateringTitle: 'Catering Services',
    cateringDesc: 'Let us bring our delicious food to your next event! Whether it\'s a wedding, corporate event, or family celebration, our catering team will make your gathering unforgettable.',
    learnMore: 'Learn More',
    
    // Menu
    menuTitle: 'Our Menu',
    menuSubtitle: 'Explore our American bar & grill favorites',
    tacos: 'Starters',
    burritos: 'Mains',
    quesadillas: 'Sandwiches',
    specialties: 'Specialties',
    
    // Locations
    locationsTitle: 'Visit Us',
    address: 'Address',
    hours: 'Hours',
    getDirections: 'Get Directions',
    callNow: 'Call Now',
    mondayWednesday: 'Monday - Wednesday',
    thursdaySaturday: 'Thursday - Saturday',
    sunday: 'Sunday',
    
    // About
    aboutTitle: 'About Capital District Social',
    aboutSubtitle: 'Where community comes together',
    ourMission: 'Our Mission',
    missionText: 'To create a welcoming social atmosphere where exceptional food, craft cocktails, and genuine hospitality come together to make every moment memorable.',
    
    // Contact
    contactTitle: 'Contact Us',
    contactSubtitle: 'We\'d love to hear from you',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    message: 'Message',
    send: 'Send Message',
    
    // Blog
    blogTitle: 'Our Blog',
    blogSubtitle: 'Stories, recipes, and updates from our kitchen',
    readMore: 'Read More',
    
    // New Home Page Sections
    orderOnline: 'Order Online',
    reservations: 'Reservations',
    ourDrinksButton: 'Our Drinks',
    requestCateringInfo: 'Request Catering Information',
    tellUsAboutEvent: 'Tell us about your event *',
    loading: 'Loading...',
    noContent: 'No homepage content configured.',
    configureSettings: 'Please configure your homepage settings in the dashboard.',
    cateringSent: 'Catering inquiry sent! We\'ll get back to you soon.',
    cateringFailed: 'Failed to send inquiry. Please try again.',
    storyImageAlt: 'Story image',
    socialImageAlt: 'Social media image',
    aboutNav: 'About Us',
    contactNav: 'Contact Us',
    cateringLink: 'Plan Your Event',
    
    // Dashboard - General
    dashboardOverview: 'Dashboard Overview',
    welcomePortal: 'Welcome to your management portal',
    welcomeToManagementPortal: 'Welcome To Management Portal',
    totalContacts: 'Total Contacts',
    formSubmissions: 'Form submissions received',
    formSubmissionsReceived: 'Form Submissions Received',
    totalPageVisits: 'Total Page Visits',
    allTimeViews: 'All time page views',
    allTimePageViews: 'All Time Page Views',
    todaysVisits: "Today's Visits",
    visitsLast24h: 'Visits in the last 24h',
    visitsInLast24h: 'Visits In Last 24h',
    uniquePages: 'Unique Pages',
    differentPagesVisited: 'Different pages visited',
    blogPosts: 'Blog Posts',
    publishedTotal: 'Published / Total posts',
    publishedTotalPosts: 'Published / Total Posts',
    todaysReservations: "Today's Reservations",
    reservationsToday: 'Reservations for today',
    reservationsForToday: 'Reservations For Today',
    recentContacts: 'Recent Contacts',
    noContactsYet: 'No contacts yet',
    noReservationsToday: 'No reservations for today',
    noReservationsForToday: 'No Reservations For Today',
    guests: 'guests',
    table: 'Table',
    
    // Dashboard - Menu Management
    menuManagement: 'Menu Management',
    manageMenuItems: 'Add, edit, and manage your menu items',
    addItem: 'Add Item',
    editItem: 'Edit Item',
    addNewItem: 'Add New Item',
    filterByCategory: 'Filter by Category:',
    allCategories: 'All Categories',
    image: 'Image',
    price: 'Price',
    category: 'Category',
    actions: 'Actions',
    noImage: 'No image',
    view: 'View',
    itemDetails: 'Item Details',
    description: 'Description',
    cancel: 'Cancel',
    update: 'Update',
    add: 'Add',
    saving: 'Saving...',
    close: 'Close',
    noMenuItems: 'No menu items found.',
    tryChangingFilter: 'Try changing the filter.',
    addFirstItem: 'Add your first item to get started!',
    loadingMenuItems: 'Loading menu items...',
    deleteConfirm: 'Are you sure you want to delete this item?',
    itemUpdated: 'Item updated successfully',
    itemAdded: 'Item added successfully',
    itemDeleted: 'Item deleted successfully',
    failedToSave: 'Failed to save item',
    failedToDelete: 'Failed to delete item',
    
    // Dashboard - Blog Management
    blogManagement: 'Blog Management',
    manageBlogPosts: 'Create, edit, and manage blog posts',
    newBlogPost: 'New Blog Post',
    createNewPost: 'Create New Blog Post',
    editBlogPost: 'Edit Blog Post',
    fillDetails: 'Fill in the details below',
    title: 'Title',
    slug: 'Slug',
    slugAutoGenerated: 'Slug * (auto-generated)',
    author: 'Author',
    excerpt: 'Excerpt',
    content: 'Content',
    visualEditor: 'Visual Editor',
    htmlEditor: 'HTML Editor',
    featuredImage: 'Featured Image',
    published: 'Published',
    draft: 'Draft',
    save: 'Save',
    uploadImage: 'Upload',
    uploading: 'Uploading...',
    maxFileSize: 'Upload an image (max 5MB) or paste a URL',
    blogCreated: 'Blog created successfully',
    blogUpdated: 'Blog updated successfully',
    blogDeleted: 'Blog deleted successfully',
    fillRequired: 'Please fill in all required fields',
    deleteBlogConfirm: 'Are you sure you want to delete this blog?',
    noBlogPosts: 'No blog posts yet. Create your first one!',
    
    // Dashboard - Reservations
    dashboardReservations: 'Reservations',
    reservationSettings: 'Reservation Settings',
    allFilter: 'All',
    booked: 'Booked',
    waitlist: 'Waitlist',
    cancelled: 'Cancelled',
    completed: 'Completed',
    allDates: 'All Dates',
    yesterday: 'Yesterday',
    today: 'Today',
    tomorrow: 'Tomorrow',
    thisMonth: 'This Month',
    pickDate: 'Pick a date',
    searchByName: 'Search by customer name...',
    firstName: 'First Name',
    lastName: 'Last Name',
    date: 'Date',
    time: 'Time',
    status: 'Status',
    type: 'Type',
    source: 'Source',
    partySize: 'Party Size',
    contactColumn: 'Contact',
    notesColumn: 'Notes',
    noReservationsFound: 'No reservations found',
    markAsBooked: 'Mark as Booked',
    markAsCompleted: 'Mark as Completed',
    markAsCancelled: 'Mark as Cancelled',
    delete: 'Delete',
    reservationUpdated: 'Reservation status updated',
    reservationDeleted: 'Reservation deleted',
    deleteReservationConfirm: 'Are you sure you want to delete this reservation?',
    loadingReservations: 'Loading reservations...',
    
    // Dashboard - Contacts
    dashboardContacts: 'Contacts',
    contactSubmissions: 'Contact Submissions',
    contactsFromMessages: 'All contacts from messages and reservations',
    addContact: 'Add Contact',
    addNewContact: 'Add New Contact',
    createNewContact: 'Create a new contact entry',
    backToContacts: 'Back to Contacts',
    deleteContact: 'Delete Contact',
    contactDetails: 'Contact Details',
    viewContactInfo: 'View and manage contact information',
    info: 'Info',
    contactInfo: 'Contact Information',
    submittedOn: 'Submitted On',
    notProvided: 'Not provided',
    messages: 'Messages',
    allContacts: 'All',
    contactAdded: 'Contact added successfully',
    contactUpdated: 'Contact updated successfully',
    contactDeleted: 'Contact deleted successfully',
    deleteContactConfirm: 'This will permanently delete the contact from',
    cannotUndo: 'This action cannot be undone.',
    newContactReceived: 'New contact received!',
    loadingContacts: 'Loading contacts...',
    backToList: 'Back to List',
    edit: 'Edit',
    manuallyAdd: 'Manually add a new contact submission',
    
    // Dashboard - Settings
    dashboardSettings: 'Settings',
    manageSettings: 'Manage your restaurant settings and preferences',
    branding: 'Branding',
    updateBranding: "Update your restaurant's logo and favicon",
    logo: 'Logo',
    logoNavBar: 'Logo (Navigation Bar)',
    favicon: 'Favicon',
    faviconBrowserTab: 'Favicon (Browser Tab Icon)',
    recommendedLogoSize: 'Recommended size: 200x80px (transparent PNG works best)',
    recommendedFaviconSize: 'Recommended size: 32x32px or 64x64px (ICO, PNG, or SVG format)',
    contactInformation: 'Contact Information',
    updateContactDetails: "Update your restaurant's contact details",
    addressField: 'Address',
    socialMedia: 'Social Media',
    updateSocialLinks: 'Update your social media links',
    facebookUrl: 'Facebook URL',
    instagramUrl: 'Instagram URL',
    twitterUrl: 'Twitter/X URL',
    tiktokUrl: 'TikTok URL',
    hoursOfOperation: 'Hours of Operation',
    updateHours: "Update your restaurant's operating hours",
    mondayWednesdayHours: 'Monday - Wednesday',
    thursdaySaturdayHours: 'Thursday - Saturday',
    sundayHours: 'Sunday',
    saveChanges: 'Save Changes',
    settingsUpdated: 'Settings updated successfully',
    logoUploaded: 'Logo uploaded and saved successfully',
    faviconUploaded: 'Favicon uploaded and saved successfully',
    imageSizeError: 'Image size must be less than 5MB',
    uploadImageFile: 'Please upload an image file',
    failedToUpload: 'Failed to upload image',
    failedToSaveSettings: 'Failed to save settings',
    
    // Dashboard - Sidebar
    management: 'Management',
    dashboardOverviewLink: 'Overview',
    homePage: 'Home Page',
    catering: 'Catering',
    dashboardAnalytics: 'Analytics',
    dashboardQuickLinks: 'Quick Links',
    mainWebsite: 'Main Website',
    dashboardLogout: 'Logout',
    login: 'Login',
    
    // Menu Categories
    appetizers: 'Appetizers',
    salads: 'Salads',
    chickenSpecialties: 'Specialties',
    taqueria: 'Taquería',
    kidsAndSidesMenu: 'Kid and Sides Menu',
    lunchMenu: 'Lunch Menu',
    desserts: 'Desserts',
    drinks: 'Drinks',
    loadingMenu: 'Loading menu...',
    noItemsInCategory: 'No items in this category yet.',
    itemAlreadyAdded: 'Item already added',
    added: 'added',
    
    // Additional missing translations
    exploreMenu: 'Explore Our Menu',
    exploreMenuDesc: "From sizzling appetizers and juicy burgers to authentic tacos and fresh salads — there's something for everyone. Dive into our full menu and find your next favorite dish.",
    viewFullMenu: 'View Full Menu',
    homeBarTitle: "Tallahassee's Home Bar",
    homeBarText1: "Capital District Social is proud to be the home bar for Tallahassee's vibrant college community. Built for students, alumni, faculty, and fans of FSU, FAMU, and TCC.",
    homeBarText2: 'From game day celebrations to weeknight hangouts, we bring people together with great food, craft cocktails, and an electric atmosphere.',
    whatWeOffer: 'What We Offer',
    bookReservation: 'Book Reservation',
    cancelReservation: 'Cancel Reservation',
    cancelYourReservation: 'Cancel Your Reservation',
    cancelReservationDesc: 'Enter the phone number and date for the reservation you want to cancel.',
    orderOnlineBtn: 'Order Online',
    callToOrder: 'Call to Order',
  },
  es: {
    // Navigation
    home: 'Inicio',
    menu: 'Menú',
    locations: 'Ubicación',
    about: 'Nosotros',
    contact: 'Contacto',
    blog: 'Blog',
    
    // Footer
    quickLinks: 'Enlaces Rápidos',
    followUs: 'Síguenos',
    contactUs: 'Contáctanos',
    rightsReserved: 'Todos los Derechos Reservados',
    
    // Home
    heroTitle: 'Cócteles Artesanales y Comida Americana Moderna',
    heroSubtitle: 'Donde la buena comida se encuentra con los buenos momentos',
    orderNow: 'Ordenar en Línea',
    viewMenu: 'Ver Menú',
    reserveTable: 'Reservar una Mesa',
    ourStory: 'Nuestra Historia',
    storyText: 'Capital District Social es más que un bar y restaurante—es un lugar de reunión comunitaria donde la buena comida se encuentra con los buenos momentos. Hemos creado un espacio donde los amigos se convierten en familia.',
    privateEvents: 'Eventos Privados',
    privateEventsDesc: '¡Nuestros espacios privados son perfectos para cualquier ocasión! Desde reuniones íntimas hasta grandes fiestas corporativas.',
    reserveNow: '¡Reserva Ahora!',
    followSocial: 'Síguenos en Redes Sociales',
    followSocialDesc: '¡Mantente conectado! Síguenos en redes sociales para no perderte nuestras últimas promociones y eventos.',
    drinksTitle: 'Cócteles Artesanales y Licores Premium Para Mantener los Buenos Momentos Fluyendo',
    ourDrinks: 'Nuestras Bebidas',
    cateringTitle: 'Servicios de Catering',
    cateringDesc: '¡Dejemos que llevemos nuestra deliciosa comida a tu próximo evento! Ya sea una boda, evento corporativo o celebración familiar.',
    learnMore: 'Saber Más',
    
    // Menu
    menuTitle: 'Nuestro Menú',
    menuSubtitle: 'Explora nuestros favoritos de bar y parrilla',
    tacos: 'Entradas',
    burritos: 'Platos Principales',
    quesadillas: 'Sándwiches',
    specialties: 'Especialidades',
    
    // Locations
    locationsTitle: 'Visítanos',
    address: 'Dirección',
    hours: 'Horario',
    getDirections: 'Cómo Llegar',
    callNow: 'Llamar Ahora',
    mondayWednesday: 'Lunes - Miércoles',
    thursdaySaturday: 'Jueves - Sábado',
    sunday: 'Domingo',
    
    // About
    aboutTitle: 'Sobre Capital District Social',
    aboutSubtitle: 'Donde la comunidad se reúne',
    ourMission: 'Nuestra Misión',
    missionText: 'Crear una atmósfera social acogedora donde la comida excepcional, los cócteles artesanales y la hospitalidad genuina se unan para hacer cada momento memorable.',
    
    // Contact
    contactTitle: 'Contáctanos',
    contactSubtitle: 'Nos encantaría saber de ti',
    name: 'Nombre',
    email: 'Correo Electrónico',
    phone: 'Teléfono',
    message: 'Mensaje',
    send: 'Enviar Mensaje',
    
    // Blog
    blogTitle: 'Nuestro Blog',
    blogSubtitle: 'Historias, recetas y actualizaciones de nuestra cocina',
    readMore: 'Leer Más',
    
    // New Home Page Sections
    orderOnline: 'Ordenar en Línea',
    reservations: 'Reservaciones',
    ourDrinksButton: 'Nuestras Bebidas',
    requestCateringInfo: 'Solicitar Información de Catering',
    tellUsAboutEvent: 'Cuéntanos sobre tu evento *',
    loading: 'Cargando...',
    noContent: 'No hay contenido de inicio configurado.',
    configureSettings: 'Por favor configura los ajustes de tu página de inicio en el panel de control.',
    cateringSent: '¡Solicitud de catering enviada! Nos pondremos en contacto pronto.',
    cateringFailed: 'Error al enviar la solicitud. Por favor intenta de nuevo.',
    storyImageAlt: 'Imagen de historia',
    socialImageAlt: 'Imagen de redes sociales',
    aboutNav: 'Sobre Nosotros',
    contactNav: 'Contáctanos',
    cateringLink: 'Planifica Tu Evento',
    
    // Dashboard - General
    dashboardOverview: 'Panel de Control',
    welcomePortal: 'Bienvenido a tu portal de gestión',
    welcomeToManagementPortal: 'Bienvenido Al Portal De Gestión',
    totalContacts: 'Contactos Totales',
    formSubmissions: 'Envíos de formularios recibidos',
    formSubmissionsReceived: 'Envíos De Formularios Recibidos',
    totalPageVisits: 'Visitas Totales',
    allTimeViews: 'Vistas de páginas de todos los tiempos',
    allTimePageViews: 'Vistas De Páginas De Todos Los Tiempos',
    todaysVisits: 'Visitas de Hoy',
    visitsLast24h: 'Visitas en las últimas 24h',
    visitsInLast24h: 'Visitas En Las Últimas 24h',
    uniquePages: 'Páginas Únicas',
    differentPagesVisited: 'Diferentes páginas visitadas',
    blogPosts: 'Publicaciones de Blog',
    publishedTotal: 'Publicadas / Total de publicaciones',
    publishedTotalPosts: 'Publicadas / Total De Publicaciones',
    todaysReservations: 'Reservaciones de Hoy',
    reservationsToday: 'Reservaciones para hoy',
    reservationsForToday: 'Reservaciones Para Hoy',
    recentContacts: 'Contactos Recientes',
    noContactsYet: 'Aún no hay contactos',
    noReservationsToday: 'No hay reservaciones para hoy',
    noReservationsForToday: 'No Hay Reservaciones Para Hoy',
    guests: 'invitados',
    table: 'Mesa',
    
    // Dashboard - Menu Management
    menuManagement: 'Gestión de Menú',
    manageMenuItems: 'Agregar, editar y gestionar elementos del menú',
    addItem: 'Agregar Artículo',
    editItem: 'Editar Artículo',
    addNewItem: 'Agregar Nuevo Artículo',
    filterByCategory: 'Filtrar por Categoría:',
    allCategories: 'Todas las Categorías',
    image: 'Imagen',
    price: 'Precio',
    category: 'Categoría',
    actions: 'Acciones',
    noImage: 'Sin imagen',
    view: 'Ver',
    itemDetails: 'Detalles del Artículo',
    description: 'Descripción',
    cancel: 'Cancelar',
    update: 'Actualizar',
    add: 'Agregar',
    saving: 'Guardando...',
    close: 'Cerrar',
    noMenuItems: 'No se encontraron artículos del menú.',
    tryChangingFilter: 'Intenta cambiar el filtro.',
    addFirstItem: '¡Agrega tu primer artículo para comenzar!',
    loadingMenuItems: 'Cargando artículos del menú...',
    deleteConfirm: '¿Estás seguro de que deseas eliminar este artículo?',
    itemUpdated: 'Artículo actualizado exitosamente',
    itemAdded: 'Artículo agregado exitosamente',
    itemDeleted: 'Artículo eliminado exitosamente',
    failedToSave: 'Error al guardar el artículo',
    failedToDelete: 'Error al eliminar el artículo',
    
    // Dashboard - Blog Management
    blogManagement: 'Gestión de Blog',
    manageBlogPosts: 'Crear, editar y gestionar publicaciones de blog',
    newBlogPost: 'Nueva Publicación de Blog',
    createNewPost: 'Crear Nueva Publicación de Blog',
    editBlogPost: 'Editar Publicación de Blog',
    fillDetails: 'Completa los detalles a continuación',
    title: 'Título',
    slug: 'URL',
    slugAutoGenerated: 'URL * (auto-generado)',
    author: 'Autor',
    excerpt: 'Extracto',
    content: 'Contenido',
    visualEditor: 'Editor Visual',
    htmlEditor: 'Editor HTML',
    featuredImage: 'Imagen Destacada',
    published: 'Publicado',
    draft: 'Borrador',
    save: 'Guardar',
    uploadImage: 'Subir',
    uploading: 'Subiendo...',
    maxFileSize: 'Sube una imagen (máx 5MB) o pega una URL',
    blogCreated: 'Blog creado exitosamente',
    blogUpdated: 'Blog actualizado exitosamente',
    blogDeleted: 'Blog eliminado exitosamente',
    fillRequired: 'Por favor completa todos los campos requeridos',
    deleteBlogConfirm: '¿Estás seguro de que deseas eliminar este blog?',
    noBlogPosts: 'Aún no hay publicaciones de blog. ¡Crea la primera!',
    
    // Dashboard - Reservations
    dashboardReservations: 'Reservaciones',
    reservationSettings: 'Configuración de Reservaciones',
    allFilter: 'Todas',
    booked: 'Reservado',
    waitlist: 'Lista de Espera',
    cancelled: 'Cancelado',
    completed: 'Completado',
    allDates: 'Todas las Fechas',
    yesterday: 'Ayer',
    today: 'Hoy',
    tomorrow: 'Mañana',
    thisMonth: 'Este Mes',
    pickDate: 'Selecciona una fecha',
    searchByName: 'Buscar por nombre del cliente...',
    firstName: 'Nombre',
    lastName: 'Apellido',
    date: 'Fecha',
    time: 'Hora',
    status: 'Estado',
    type: 'Tipo',
    source: 'Fuente',
    partySize: 'Tamaño del Grupo',
    contactColumn: 'Contacto',
    notesColumn: 'Notas',
    noReservationsFound: 'No se encontraron reservaciones',
    markAsBooked: 'Marcar como Reservado',
    markAsCompleted: 'Marcar como Completado',
    markAsCancelled: 'Marcar como Cancelado',
    delete: 'Eliminar',
    reservationUpdated: 'Estado de reservación actualizado',
    reservationDeleted: 'Reservación eliminada',
    deleteReservationConfirm: '¿Estás seguro de que deseas eliminar esta reservación?',
    loadingReservations: 'Cargando reservaciones...',
    
    // Dashboard - Contacts
    dashboardContacts: 'Contactos',
    contactSubmissions: 'Envíos de Contacto',
    contactsFromMessages: 'Todos los contactos de mensajes y reservaciones',
    addContact: 'Agregar Contacto',
    addNewContact: 'Agregar Nuevo Contacto',
    createNewContact: 'Crear una nueva entrada de contacto',
    backToContacts: 'Volver a Contactos',
    deleteContact: 'Eliminar Contacto',
    contactDetails: 'Detalles del Contacto',
    viewContactInfo: 'Ver y gestionar información de contacto',
    info: 'Información',
    contactInfo: 'Información de Contacto',
    submittedOn: 'Enviado el',
    notProvided: 'No proporcionado',
    messages: 'Mensajes',
    allContacts: 'Todos',
    contactAdded: 'Contacto agregado exitosamente',
    contactUpdated: 'Contacto actualizado exitosamente',
    contactDeleted: 'Contacto eliminado exitosamente',
    deleteContactConfirm: 'Esto eliminará permanentemente el contacto de',
    cannotUndo: 'Esta acción no se puede deshacer.',
    newContactReceived: '¡Nuevo contacto recibido!',
    loadingContacts: 'Cargando contactos...',
    backToList: 'Volver a la Lista',
    edit: 'Editar',
    manuallyAdd: 'Agregar manualmente un nuevo envío de contacto',
    
    // Dashboard - Settings
    dashboardSettings: 'Configuración',
    manageSettings: 'Gestiona la configuración y preferencias de tu restaurante',
    branding: 'Marca',
    updateBranding: 'Actualiza el logo y favicon de tu restaurante',
    logo: 'Logo',
    logoNavBar: 'Logo (Barra de Navegación)',
    favicon: 'Favicon',
    faviconBrowserTab: 'Favicon (Icono de Pestaña del Navegador)',
    recommendedLogoSize: 'Tamaño recomendado: 200x80px (PNG transparente funciona mejor)',
    recommendedFaviconSize: 'Tamaño recomendado: 32x32px o 64x64px (formato ICO, PNG o SVG)',
    contactInformation: 'Información de Contacto',
    updateContactDetails: 'Actualiza los detalles de contacto de tu restaurante',
    addressField: 'Dirección',
    socialMedia: 'Redes Sociales',
    updateSocialLinks: 'Actualiza tus enlaces de redes sociales',
    facebookUrl: 'URL de Facebook',
    instagramUrl: 'URL de Instagram',
    twitterUrl: 'URL de Twitter/X',
    tiktokUrl: 'URL de TikTok',
    hoursOfOperation: 'Horario de Operación',
    updateHours: 'Actualiza el horario de operación de tu restaurante',
    mondayWednesdayHours: 'Lunes - Miércoles',
    thursdaySaturdayHours: 'Jueves - Sábado',
    sundayHours: 'Domingo',
    saveChanges: 'Guardar Cambios',
    settingsUpdated: 'Configuración actualizada exitosamente',
    logoUploaded: 'Logo subido y guardado exitosamente',
    faviconUploaded: 'Favicon subido y guardado exitosamente',
    imageSizeError: 'El tamaño de la imagen debe ser menor a 5MB',
    uploadImageFile: 'Por favor sube un archivo de imagen',
    failedToUpload: 'Error al subir la imagen',
    failedToSaveSettings: 'Error al guardar la configuración',
    
    // Dashboard - Sidebar
    management: 'Gestión',
    dashboardOverviewLink: 'Resumen',
    homePage: 'Página de Inicio',
    catering: 'Catering',
    dashboardAnalytics: 'Analíticas',
    dashboardQuickLinks: 'Enlaces Rápidos',
    mainWebsite: 'Sitio Web Principal',
    dashboardLogout: 'Cerrar Sesión',
    login: 'Iniciar Sesión',
    
    // Menu Categories
    appetizers: 'Aperitivos',
    salads: 'Ensaladas',
    chickenSpecialties: 'Especialidades',
    taqueria: 'Taquería',
    kidsAndSidesMenu: 'Menú Infantil y Acompañantes',
    lunchMenu: 'Menú de Almuerzo',
    desserts: 'Postres',
    drinks: 'Bebidas',
    loadingMenu: 'Cargando menú...',
    noItemsInCategory: 'Aún no hay artículos en esta categoría.',
    itemAlreadyAdded: 'Artículo ya agregado',
    added: 'agregado',
    
    // Additional missing translations
    exploreMenu: 'Explora Nuestro Menú',
    exploreMenuDesc: 'Desde aperitivos chispeantes y jugosas hamburguesas hasta tacos auténticos y ensaladas frescas — hay algo para todos. Descubre nuestro menú completo y encuentra tu próximo plato favorito.',
    viewFullMenu: 'Ver Menú Completo',
    homeBarTitle: 'El Bar Local de Tallahassee',
    homeBarText1: 'Capital District Social se enorgullece de ser el bar local para la vibrante comunidad universitaria de Tallahassee. Hecho para estudiantes, exalumnos, profesores y fanáticos de FSU, FAMU y TCC.',
    homeBarText2: 'Desde celebraciones de día de juego hasta reuniones entre semana, unimos a la gente con buena comida, cócteles artesanales y una atmósfera eléctrica.',
    whatWeOffer: 'Lo Que Ofrecemos',
    bookReservation: 'Reservar',
    cancelReservation: 'Cancelar Reservación',
    cancelYourReservation: 'Cancelar Tu Reservación',
    cancelReservationDesc: 'Ingresa el número de teléfono y la fecha de la reservación que deseas cancelar.',
    orderOnlineBtn: 'Ordenar en Línea',
    callToOrder: 'Llamar para Ordenar',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.en] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
